import { useTranslation } from 'react-i18next';
import Button from '@/components/Button';
import EmptyState, { type EmptyStateFeature } from '@/components/EmptyState';
import DocumentsIllustration from '@/components/EmptyState/illustrations/DocumentsIllustration';
import IcExport from '@/assets/icons/Export.svg?react';
import IcPlus from '@/assets/icons/Plus.svg?react';
import IcReload from '@/assets/icons/Reload.svg?react';
import IcUsers from '@/assets/icons/Users.svg?react';
import styles from './ListItemEmpty.module.scss';
import preloadChunks from '@/routes/preloadChunks.util';

type EmptyType =
  | 'all'
  | 'draft'
  | 'pending'
  | 'done'
  | 'expired'
  | 'cancelled'
  | 'multi';

interface ListItemEmptyProps {
  type: EmptyType;
}

const TYPES_WITH_CTA = new Set<EmptyType>([
  'all',
  'draft',
  'multi',
  'pending',
  'done',
]);
const UPLOAD_CTA_TYPES = new Set<EmptyType>(['all', 'draft', 'multi']);
const FALLBACK_KEY_TYPES = new Set<EmptyType>(['all', 'multi']);

const ctaLabelKey = (type: EmptyType): string =>
  UPLOAD_CTA_TYPES.has(type)
    ? 'signings:empty.cta.upload'
    : 'signings:empty.cta.send';

const titleKeyFor = (type: EmptyType): string =>
  FALLBACK_KEY_TYPES.has(type)
    ? 'signings:empty.draft.title'
    : `signings:empty.${type}.title`;

const subtitleKeyFor = (type: EmptyType): string =>
  FALLBACK_KEY_TYPES.has(type)
    ? 'signings:empty.draft.subtitle'
    : `signings:empty.${type}.subtitle`;

const ListItemEmpty = ({ type }: ListItemEmptyProps) => {
  const { t } = useTranslation(['signings']);

  const features: EmptyStateFeature[] = [
    {
      key: 'drag',
      icon: IcExport,
      title: t('signings:empty.features.drag.title'),
      desc: t('signings:empty.features.drag.desc'),
    },
    {
      key: 'signers',
      icon: IcUsers,
      title: t('signings:empty.features.signers.title'),
      desc: t('signings:empty.features.signers.desc'),
    },
    {
      key: 'track',
      icon: IcReload,
      title: t('signings:empty.features.track.title'),
      desc: t('signings:empty.features.track.desc'),
    },
  ];

  const handlePreloadSigning = () => {
    void preloadChunks['signings.workflow']();
  };

  const cta = TYPES_WITH_CTA.has(type) ? (
    <Button
      as="link"
      to="new"
      variant="primary"
      size="slim"
      onMouseEnter={handlePreloadSigning}
      onFocus={handlePreloadSigning}
    >
      <IcPlus className={styles.ctaIcon} aria-hidden="true" />
      {t(ctaLabelKey(type))}
    </Button>
  ) : null;

  return (
    <EmptyState
      illustration={<DocumentsIllustration />}
      title={t(titleKeyFor(type))}
      subtitle={t(subtitleKeyFor(type))}
      cta={cta}
      features={features}
    />
  );
};

export default ListItemEmpty;
